import React, { useContext } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import PropTypes from 'prop-types';
// Core components
import Avatar from 'components/core/Avatar';
import MenuButton from 'components/core/MenuButton';
import { TextButton } from 'components/core/Button';
// HOCS
import withPermissions from 'hocs/WithPermissions';
// Hooks
import useResponsive from 'hooks/useResponsive';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mAuth from 'models/auth';
// Custom
import { postLogout } from 'actions';
import { meDataShape } from 'constants/PropTypesShapes';
import LangSelector from 'components/misc/LangSelector';
import { accountPath } from 'account/navigation';
import { listMyBookingsPath } from 'cpmMyBookings/navigation';
import { bookCarparksListPath } from 'cpmBook/navigation';
import { CpmClientContext } from 'components/misc/Contexts';

const getFirstLetter = (meData) => {
  if (meData && meData.firstname) return meData.firstname.charAt(0);
  if (meData && meData.lastname) return meData.lastname.charAt(0);
  return '?';
};

const CpmClientNavRightList = ({
  meData,
  token,
  dispatchPostLogout,
  hasPermission,
}) => {
  const avatarLetter = getFirstLetter(meData).toUpperCase();
  // Permissions
  const { pFeature } = permissions;
  const tokenIsUser = mAuth.getTokenIsUser(token);
  // Values from context
  const { showBookButton } = useContext(CpmClientContext);
  const isMobile = useResponsive();

  /* ========================================================= */
  /* SUB COMPONENTS                                            */
  /* ========================================================= */

  const AvatarButton = (
    <Avatar size={32} primary>
      {avatarLetter}
    </Avatar>
  );

  const myBookingsItem = hasPermission({ needed: pFeature.bookings, permissionsOrContext: 'meFeatures' })
    ? {
      key: 'itemListMyBookings',
      label: <LocalizedMessage id="menu.listMyBookings" />,
      icon: 'btnBookings',
      path: listMyBookingsPath(),
      visible: true,
    }
    : null;

  const myAccountItem = {
    key: 'itemMyAccount',
    label: <LocalizedMessage id="menu.myAccount" />,
    icon: 'btnManage',
    path: accountPath(),
    visible: true,
  };

  const logoutItem = {
    key: 'itemLogout',
    label: <LocalizedMessage id="menu.logout" />,
    icon: 'btnPower',
    handleAction: dispatchPostLogout,
    visible: true,
  };

  const bookingButtonDesktopStyle = {};

  const bookingButtonMobileStyle = {
    position: 'fixed',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 999,
  };

  const BookingButton = showBookButton ? (
    <OnlyWith
      permissions={{
        needed: pFeature.bookings,
        permissionsOrContext: 'meFeatures',
      }}
    >
      <div style={isMobile ? bookingButtonMobileStyle : bookingButtonDesktopStyle}>
        <TextButton
          label={<LocalizedMessage id="menu.book" />}
          variant={isMobile ? 'contained' : 'outlined'}
          path={bookCarparksListPath()}
        />
      </div>
    </OnlyWith>
  ) : null;

  /* ========================================================= */
  /* UI                                                        */
  /* ========================================================= */

  return (
    <div className="d-flex align-items-center justify-content-end ml-auto" style={{ gap: '15px' }}>
      {meData && tokenIsUser && (
        <React.Fragment>
          {BookingButton}
          <MenuButton
            buttonComp={AvatarButton}
            items={[
              myBookingsItem,
              myAccountItem,
              logoutItem,
            ]}
            withoutObj
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          />
        </React.Fragment>
      )}
      <LangSelector />
    </div>
  );
};

/* ========================================================= */
/* PROP TYPES                                                */
/* ========================================================= */

CpmClientNavRightList.propTypes = {
  meData: meDataShape,
  token: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  dispatchPostLogout: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
};

CpmClientNavRightList.defaultProps = {
  token: '',
  meData: null,
};

/* ========================================================= */
/* STORE                                                     */
/* ========================================================= */

const mapStateToProps = (state) => ({
  token: state.auth.token,
  meData: state.me.data,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchPostLogout: () => {
    dispatch(postLogout());
  },
});

/* ========================================================= */
/* EXPORT                                                    */
/* ========================================================= */

module.exports = compose(connect(mapStateToProps, mapDispatchToProps), withPermissions, withRouter)(CpmClientNavRightList);



// WEBPACK FOOTER //
// ./components/header/CpmClientNavRightList.js